import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

// The app's media area isn't a fixed ratio across every device (it's the
// screen height minus a fixed text-section height, so it varies slightly
// by device) — this uses a representative ratio based on a typical phone
// (Pixel 8: 412 wide / 622 available height after the fixed text section)
// so the crop preview closely matches what vendors' photos will actually
// look like once cropped on a real device.
export const TARGET_ASPECT_RATIO = 412 / 622;

export default function ImageCropModal({ imageSrc, initialCrop, initialZoom, onCancel, onConfirm }) {
  const [crop, setCrop] = useState(initialCrop || { x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialZoom || 1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = () => {
    if (!croppedAreaPixels) return;
    onConfirm({
      cropX: Math.round(croppedAreaPixels.x),
      cropY: Math.round(croppedAreaPixels.y),
      cropWidth: Math.round(croppedAreaPixels.width),
      cropHeight: Math.round(croppedAreaPixels.height),
      // Saved alongside the pixel region specifically so re-opening this
      // image for editing later can restore the exact same pan/zoom
      // state, rather than resetting to a default center/1x view.
      panX: crop.x,
      panY: crop.y,
      zoom,
    });
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={styles.title}>Position Your Photo</h3>
        <p style={styles.subtitle}>
          Drag to reposition, scroll or pinch to zoom. The frame shows what couples will actually see.
        </p>
        <div style={styles.cropperWrapper}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={TARGET_ASPECT_RATIO}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </div>
        <div style={styles.zoomRow}>
          <span style={styles.zoomLabel}>Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={styles.zoomSlider}
          />
        </div>
        <div style={styles.buttonRow}>
          <button onClick={onCancel} style={styles.cancelButton}>Cancel</button>
          <button onClick={handleConfirm} style={styles.confirmButton}>Use This Photo</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '420px', display: 'flex', flexDirection: 'column' },
  title: { margin: 0, fontSize: '18px', fontWeight: '700', color: '#000' },
  subtitle: { margin: '6px 0 16px', fontSize: '13px', color: '#666', lineHeight: 1.4 },
  cropperWrapper: { position: 'relative', width: '100%', height: '360px', backgroundColor: '#000', borderRadius: '10px', overflow: 'hidden' },
  zoomRow: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px' },
  zoomLabel: { fontSize: '13px', color: '#666', minWidth: '36px' },
  zoomSlider: { flex: 1 },
  buttonRow: { display: 'flex', gap: '10px', marginTop: '20px' },
  cancelButton: { flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #ddd', backgroundColor: '#fff', color: '#000', fontWeight: '600', cursor: 'pointer' },
  confirmButton: { flex: 1, padding: '12px', borderRadius: '10px', border: 'none', backgroundColor: '#000', color: '#fff', fontWeight: '600', cursor: 'pointer' },
};